import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Mashup palette: warm cream + deep navy + terracotta toscana ─── */
const M = {
  cream: '#FAF9F6',
  warmWhite: '#F4F1EC',
  navy: '#1B2A4A',
  navyLight: '#2C3E5A',
  navyMuted: '#3D5170',
  terracotta: '#C4532D',
  terracottaLight: '#D4714F',
  teal: '#1A7A6D',
  gold: '#B8963E',
  text: '#1B2A4A',
  textSecondary: '#5A6B80',
  textMuted: '#8A96A5',
  border: '#E2DED6',
  borderLight: '#EDEBE6',
  white: '#FFFFFF',
};

const fontLink = "https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600;700;900&family=Titillium+Web:wght@300;400;600;700&display=swap";

/* ─── Nav ─── */
const MashupNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const links = [
    { label: 'Doposcuola', to: '/doposcuola' },
    { label: 'Formazione', to: '/formazione' },
    { label: 'Chi Siamo', to: '/about' },
    { label: 'Dona', to: '/dona' },
  ];

  return (
    <nav data-testid="mashup-nav" style={{ fontFamily: "'Titillium Web', sans-serif" }}
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300`}>
      <div style={{ background: M.terracotta, height: '3px' }} />
      <div style={{
        background: scrolled ? 'rgba(250,249,246,0.97)' : M.cream,
        borderBottom: `1px solid ${M.border}`,
        transition: 'all 0.3s'
      }} className={scrolled ? 'shadow-sm' : ''}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/versione-c" className="flex items-center gap-3" data-testid="mashup-logo">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: M.navy }}>
              <span className="text-white font-black text-xs" style={{ fontFamily: "'Source Serif 4', serif" }}>AI</span>
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight" style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>
                &lt;/AI_covo.tech&gt;<sup style={{ fontSize: '8px' }}>®</sup>
              </span>
              <span className="block text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: M.terracotta }}>
                Hub Maieutica Digitale
              </span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {links.map(l => (
              <Link key={l.label} to={l.to}
                className="text-sm font-semibold transition-colors"
                style={{ color: M.textSecondary }}
                onMouseEnter={e => e.target.style.color = M.terracotta}
                onMouseLeave={e => e.target.style.color = M.textSecondary}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Link to="/test-ab" className="hidden sm:inline-flex text-xs px-4 py-2 rounded-lg border font-semibold transition-all hover:scale-105"
              style={{ borderColor: M.navy, color: M.navy }} data-testid="mashup-back-ab">
              A/B Test
            </Link>
            <button data-testid="mashup-mobile-menu" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}
              style={{ color: M.navy }}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: M.cream, borderBottom: `1px solid ${M.border}` }} className="md:hidden shadow-lg">
          {links.map(l => (
            <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)}
              className="block px-6 py-4 text-sm font-semibold border-b"
              style={{ color: M.textSecondary, borderColor: M.borderLight }}>
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};

/* ─── Hero: cream bg, left-aligned, serif headline ─── */
const MashupHero = () => (
  <section data-testid="mashup-hero" className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ background: M.cream }}>
    {/* Subtle dot pattern */}
    <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: `radial-gradient(circle at 1px 1px, ${M.navy} 0.5px, transparent 0)`,
      backgroundSize: '32px 32px'
    }} />
    {/* Warm terracotta glow top-right */}
    <div className="absolute top-0 right-0 w-1/3 h-1/2 opacity-[0.06]"
      style={{ background: `radial-gradient(ellipse at 80% 20%, ${M.terracotta}, transparent 70%)` }} />

    <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-20 w-full">
      <div className="grid lg:grid-cols-5 gap-12 items-center">
        {/* Left: text (3 cols) */}
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8"
            style={{ background: M.terracotta + '12', border: `1px solid ${M.terracotta}25` }}>
            <div className="w-2 h-2 rounded-full" style={{ background: M.terracotta }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: M.terracotta, fontFamily: "'Titillium Web'" }}>
              Valdelsa, Toscana
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] mb-6"
            style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>
            La scuola dei<br />
            <span style={{ color: M.terracotta }}>visionari.</span>
          </h1>
          <p className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg"
            style={{ color: M.textSecondary, fontFamily: "'Titillium Web', sans-serif" }}>
            Un hub dove la maieutica incontra il digitale. Doposcuola, formazione,
            inclusione e cultura del fare — perché il sapere non ha età.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/doposcuola" data-testid="mashup-cta-scopri"
              className="inline-flex items-center gap-2 px-8 py-4 font-bold text-sm uppercase tracking-wider rounded-lg transition-all hover:shadow-lg hover:scale-[1.02]"
              style={{ background: M.terracotta, color: M.white, fontFamily: "'Titillium Web'" }}>
              Scopri il Progetto
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </Link>
            <Link to="/dona" data-testid="mashup-cta-dona"
              className="inline-flex items-center px-8 py-4 font-bold text-sm uppercase tracking-wider rounded-lg border-2 transition-all hover:shadow-md hover:scale-[1.02]"
              style={{ borderColor: M.navy, color: M.navy, fontFamily: "'Titillium Web'" }}>
              Sostienici
            </Link>
          </div>
        </div>
        {/* Right: key numbers (2 cols) */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          {[
            { num: '6-18', label: 'Anni Doposcuola', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
            { num: '100%', label: 'Deducibile', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { num: '5+', label: 'Comunità attive', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
            { num: '0€', label: 'Per chi ha bisogno', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
          ].map((item, i) => (
            <div key={i} className="p-5 rounded-xl border text-center transition-all hover:-translate-y-0.5 hover:shadow-md"
              style={{ background: M.white, borderColor: M.borderLight }}>
              <svg className="w-7 h-7 mx-auto mb-2" style={{ color: M.terracotta }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
              </svg>
              <p className="text-2xl font-black" style={{ color: M.navy }}>{item.num}</p>
              <p className="text-xs font-semibold mt-1" style={{ color: M.textMuted }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── MISSION: Hub Digitale + Maieutica (navy full-width) ─── */
const MashupMission = () => (
  <section data-testid="mashup-mission" className="py-20 relative overflow-hidden" style={{ background: M.navy }}>
    <div className="absolute inset-0 opacity-[0.06]" style={{
      backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 0.5px, transparent 0)`,
      backgroundSize: '28px 28px'
    }} />
    <div className="relative max-w-6xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-16">
        {/* Hub Digitale */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: M.terracotta }}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Source Serif 4', serif" }}>
              Hub Digitale
            </h2>
          </div>
          <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Titillium Web'" }}>
            Un centro che agisce da <strong className="text-white">snodo centrale</strong> per connettere
            tecnologie, dati, persone e imprese. Facilitiamo la <strong className="text-white">trasformazione digitale</strong>,
            la collaborazione e lo scambio di informazioni.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Titillium Web'" }}>
            Un ecosistema per l'innovazione, che supporta le PMI nel percorso
            verso l'Industria 4.0 — partendo dal territorio della Valdelsa.
          </p>
        </div>

        {/* Hub Maieutica */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: M.gold }}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Source Serif 4', serif" }}>
              Hub Maieutica
            </h2>
          </div>
          <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Titillium Web'" }}>
            L'arte di <strong className="text-white">tirar fuori il potenziale</strong> da ogni persona.
            Come Socrate con i suoi allievi, crediamo che ognuno abbia già dentro di sé
            il proprio valore — il nostro compito è <strong className="text-white">farlo emergere</strong>.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Titillium Web'" }}>
            Doposcuola maieutico, tutoraggio personalizzato, supporto DSA,
            formazione digitale per anziani — nessuno resta indietro.
          </p>
        </div>
      </div>

      {/* Bottom quote */}
      <div className="mt-16 pt-10 border-t text-center" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        <blockquote className="text-xl sm:text-2xl font-bold italic leading-snug mb-4"
          style={{ color: M.gold, fontFamily: "'Source Serif 4', serif" }}>
          "La maieutica è l'arte di far partorire le menti,
          di tirare fuori ciò che è già dentro."
        </blockquote>
        <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: M.terracotta }}>
          Socrate
        </p>
      </div>
    </div>
  </section>
);

/* ─── Servizi: cream bg, mixed cards ─── */
const MashupServices = () => {
  const services = [
    { icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', title: 'Doposcuola', desc: 'Supporto quotidiano per bambini e ragazzi 6-18 anni. Metodo maieutico.', accent: M.terracotta },
    { icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', title: 'Formazione Digitale', desc: 'Corsi detraibili al 100% per professionisti e PMI.', accent: M.teal },
    { icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', title: 'Inclusione', desc: 'Spazi per anziani, famiglie ISEE, persone con disabilità.', accent: '#7C3AED' },
    { icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z', title: 'Laboratori & Arti', desc: 'Arti perdute, artigianato, creatività e innovazione.', accent: M.gold },
    { icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9', title: 'Cittadino Digitale', desc: 'Alfabetizzazione per chi è rimasto indietro nel digitale.', accent: '#3B82F6' },
    { icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064', title: 'Sport & Benessere', desc: 'Attività sportive e weekend per tutta la comunità.', accent: '#F97316' },
  ];

  return (
    <section data-testid="mashup-services" className="py-20" style={{ background: M.cream }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3"
            style={{ color: M.terracotta, fontFamily: "'Titillium Web'" }}>I Nostri Pilastri</p>
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>
            Un ecosistema per <span style={{ color: M.terracotta }}>tutti.</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="p-6 rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group"
              style={{ background: M.white, borderColor: M.borderLight }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ background: s.accent + '12' }}>
                <svg className="w-6 h-6" style={{ color: s.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={s.icon} />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>{s.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: M.textSecondary, fontFamily: "'Titillium Web'" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Community Counter ─── */
const MashupCommunity = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/community/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const sections = [
    { key: 'genz', label: 'Agora GenZ', count: stats?.genz || 0, accent: '#EAB308' },
    { key: 'boomer', label: 'Boomer & GenX', count: stats?.boomer || 0, accent: '#B8963E' },
    { key: 'senatori', label: 'I Senatori', count: stats?.senatori || 0, accent: '#94A3B8' },
    { key: 'inclusivo', label: 'Spazio Inclusivo', count: stats?.inclusivo || 0, accent: '#A855F7' },
    { key: 'sport', label: 'Sport', count: stats?.sport || 0, accent: '#F97316' },
  ];
  const total = sections.reduce((s, x) => s + x.count, 0);

  return (
    <section data-testid="mashup-community" className="py-20" style={{ background: M.warmWhite }}>
      <div className="max-w-5xl mx-auto px-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3"
          style={{ color: M.terracotta, fontFamily: "'Titillium Web'" }}>La Comunità Cresce</p>
        <p className="text-5xl sm:text-6xl font-black mb-2" style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>{total}</p>
        <p className="text-sm mb-10" style={{ color: M.textSecondary, fontFamily: "'Titillium Web'" }}>anime al Covo — in tempo reale</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {sections.map(s => (
            <div key={s.key} className="p-4 rounded-xl border text-center transition-all hover:-translate-y-0.5"
              style={{ background: M.white, borderColor: M.borderLight }}>
              <p className="text-2xl font-black" style={{ color: s.accent }}>{s.count}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold mt-1" style={{ color: M.textMuted }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CTA Sostienici ─── */
const MashupCTA = () => (
  <section data-testid="mashup-cta" className="py-24" style={{ background: M.cream }}>
    <div className="max-w-4xl mx-auto px-6 text-center">
      <div className="p-10 sm:p-14 rounded-2xl border relative overflow-hidden" style={{ background: M.white, borderColor: M.border }}>
        {/* Subtle accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: `linear-gradient(90deg, ${M.terracotta}, ${M.gold}, ${M.teal})` }} />

        <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-4"
          style={{ color: M.teal, fontFamily: "'Titillium Web'" }}>Sostienici</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>
          Che cintura sei?
        </h2>
        <p className="text-base mb-10 max-w-lg mx-auto" style={{ color: M.textSecondary, fontFamily: "'Titillium Web'" }}>
          Come nel karate, ogni livello ha il suo valore. Da 1€ a 999€ aziendale —
          tutto <strong style={{ color: M.navy }}>deducibile al 100%</strong>.
        </p>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {[
            { belt: 'Bianca', price: '1€', color: '#9CA3AF' },
            { belt: 'Arancione', price: '5€', color: '#F97316' },
            { belt: 'Verde', price: '10€', color: '#22C55E' },
            { belt: 'Blu', price: '15€', color: '#3B82F6' },
            { belt: 'Rossa', price: '50€', color: '#EF4444' },
          ].map(b => (
            <div key={b.belt} className="px-5 py-3 rounded-xl border text-center min-w-[80px] transition-all hover:-translate-y-0.5"
              style={{ borderColor: b.color + '30', background: M.warmWhite }}>
              <p className="text-lg font-black" style={{ color: b.color }}>{b.price}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: M.textMuted }}>{b.belt}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/dona" data-testid="mashup-dona-cta"
            className="inline-flex items-center gap-2 px-10 py-4 font-bold text-sm uppercase tracking-wider rounded-lg transition-all hover:shadow-lg hover:scale-[1.02]"
            style={{ background: M.terracotta, color: M.white, fontFamily: "'Titillium Web'" }}>
            Dona Ora
          </Link>
          <Link to="/dona" data-testid="mashup-sponsor-cta"
            className="inline-flex items-center gap-2 px-10 py-4 font-bold text-sm uppercase tracking-wider rounded-lg border-2 transition-all hover:shadow-md hover:scale-[1.02]"
            style={{ borderColor: M.navy, color: M.navy, fontFamily: "'Titillium Web'" }}>
            Sponsor 999€
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Info Band ─── */
const MashupInfoBand = () => (
  <section data-testid="mashup-info-band" className="py-14" style={{ background: M.navy }}>
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid sm:grid-cols-3 gap-8 text-center">
        {[
          { label: 'Associazione Culturale', desc: 'Iscritta al Registro', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
          { label: 'Erogazioni Liberali', desc: 'Deducibili al 100%', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
          { label: 'Aperto a Tutti', desc: 'Nessun requisito di accesso', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
        ].map((item, i) => (
          <div key={i} className="flex flex-col items-center">
            <svg className="w-8 h-8 mb-3" style={{ color: M.terracotta }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
            </svg>
            <p className="text-lg font-bold text-white" style={{ fontFamily: "'Source Serif 4', serif" }}>{item.label}</p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Titillium Web'" }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Footer ─── */
const MashupFooter = () => (
  <footer data-testid="mashup-footer" className="py-16 border-t" style={{ background: M.cream, borderColor: M.border }}>
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-10 mb-12">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: M.navy }}>
              <span className="text-white font-black text-xs" style={{ fontFamily: "'Source Serif 4', serif" }}>AI</span>
            </div>
            <span className="font-bold" style={{ color: M.navy, fontFamily: "'Source Serif 4', serif" }}>AI_covo.tech</span>
          </div>
          <p className="text-sm" style={{ color: M.textMuted, fontFamily: "'Titillium Web'" }}>
            Hub Maieutica Digitale<br />
            Colle di Val d'Elsa (SI)<br />
            Toscana, Italia
          </p>
        </div>
        {[
          { title: 'Servizi', links: [['Doposcuola', '/doposcuola'], ['Formazione', '/formazione'], ['Inclusione', '/hub'], ['Artistigianato', '/artistigianato']] },
          { title: 'Risorse', links: [['Brand Kit', '/brand'], ['Sostienici', '/dona'], ['RoadMap', '/links']] },
          { title: 'Contatti', links: [['info@aicovo.it', '#'], ['WhatsApp', 'https://wa.me/393273839782']] },
        ].map(col => (
          <div key={col.title}>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: M.terracotta }}>{col.title}</h4>
            <ul className="space-y-2">
              {col.links.map(([label, href]) => (
                <li key={label}>
                  <Link to={href} className="text-sm transition-colors"
                    style={{ color: M.textMuted, fontFamily: "'Titillium Web'" }}
                    onMouseEnter={e => e.target.style.color = M.navy}
                    onMouseLeave={e => e.target.style.color = M.textMuted}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderColor: M.border }}>
        <p className="text-xs" style={{ color: M.textMuted }}>© 2030 AI_COVO · Hub Maieutica Digitale</p>
        <p className="text-xs" style={{ color: M.textMuted }}>&lt;/BUILT_FOR_COMMUNITY/&gt;</p>
      </div>
    </div>
  </footer>
);

/* ─── Main Page ─── */
const VersioneMashupPage = () => (
  <div data-testid="versione-mashup-page" style={{ background: M.cream, color: M.text }}>
    <link href={fontLink} rel="stylesheet" />
    <MashupNav />
    <MashupHero />
    <MashupMission />
    <MashupServices />
    <MashupCommunity />
    <MashupCTA />
    <MashupInfoBand />
    <MashupFooter />
  </div>
);

export default VersioneMashupPage;
